import { commonServerInstance } from '@/utilities/instances'

// GET /api/v1/pricing/price-cards — list/search driver pricing price cards
//
// `zipcode: null` rows are billing-owner-level fallback prices; `zipcode` set
// rows are zipcode-level overrides layered on top of the fallback.
// Soft-deleted rows are excluded unless `includeDeleted=true`.
const PRICE_CARDS_PATH = '/api/v1/pricing/price-cards'

export type PriceCardSortBy =
  | 'updatedAt'
  | 'createTime'
  | 'begDate'
  | 'endDate'
  | 'unitPrice'
  | 'billingOwner'
  | 'zipcode'

export type PriceCardSortDir = 'asc' | 'desc'

export interface FetchPriceCardsParams {
  id?: number
  billingOwner?: string
  zipcode?: string
  /** Y-m-d, e.g. "2026-09-01" — other formats are rejected by the API. */
  effectiveOn?: string
  includeDeleted?: boolean
  page?: number
  pageSize?: number
  sortBy?: PriceCardSortBy
  sortDir?: PriceCardSortDir
}

/** Raw row shape as returned by the API — see the sample response captured
 * from the endpoint's docs for the exact fields/casing. */
export interface PriceCardApiRow {
  id: number
  billingOwner: string
  zipcode: string | null
  weightType: string
  minWeight: number
  maxWeight: number
  weightUnit: string
  unitCurrency: string
  unitPrice: number
  begDate: string
  endDate: string
  priceCardType: string
  neverEffective: boolean
  createdBy: string | null
  createTime: string
  changedBy: string | null
  lastModifyTime: string
  isDeleted: boolean
}

export interface FetchPriceCardsResult {
  data: PriceCardApiRow[]
  page: number
  pageSize: number
  total: number
}

export const fetchPriceCards = async (
  params: FetchPriceCardsParams
): Promise<FetchPriceCardsResult> => {
  try {
    const response = await commonServerInstance.get(PRICE_CARDS_PATH, {
      params
    })
    // Envelope: { code, message, data: { data: [...], page, pageSize, total } }
    return response?.data?.data
  } catch (error) {
    console.error('Fetch Price Cards Error:', error)
    throw error
  }
}

// POST /api/v1/pricing/price-cards — create a billing-owner-level fallback
// (zipcode null/omitted) or zipcode-level override (zipcode set) price
// card. The endpoint enforces the retroactive-edit lock, overlap/continuity
// checks, insert-time close-out of the prior open-ended record for the
// same key, and never-effective handling (begDate > endDate is accepted
// and stored but excluded from active-pricing lookups).
export interface CreatePriceCardPayload {
  billingOwner: string
  /** null for a billing-owner-level fallback card. */
  zipcode: string | null
  /** Legacy descriptive label (e.g. "0-5 LBS") mirroring minWeight/maxWeight
   * — the create response has been observed to null this out, so it may
   * not actually be persisted, but the endpoint still accepts it. */
  weightType: string | null
  weightUnit: string
  minWeight: number
  maxWeight?: number
  unitCurrency: string
  unitPrice: number
  /** Y-m-d. */
  begDate: string
  /** Y-m-d — use the '9999-12-31' sentinel for "no end date". */
  endDate: string
}

export const createPriceCard = async (
  payload: CreatePriceCardPayload
): Promise<PriceCardApiRow> => {
  try {
    const response = await commonServerInstance.post(PRICE_CARDS_PATH, payload)
    // Envelope: { code, message, data: <PriceCardApiRow> }
    return response?.data?.data
  } catch (error) {
    console.error('Create Price Card Error:', error)
    throw error
  }
}

// PATCH /api/v1/pricing/price-cards/{id} — update an existing price card's
// rate/effective-period terms. billingOwner and zipcode aren't included: a
// card's identity (which owner/zipcode it belongs to) can't be changed by
// an edit, only its terms. The weight interval (weightType/weightUnit/
// minWeight/maxWeight) also can't be changed by an edit — the endpoint no
// longer accepts them here; it's fixed at creation time only.
export interface UpdatePriceCardPayload {
  unitCurrency: string
  unitPrice: number
  /** Y-m-d. */
  begDate: string
  /** Y-m-d — use the '9999-12-31' sentinel for "no end date". */
  endDate: string
}

export const updatePriceCard = async (
  id: number,
  payload: UpdatePriceCardPayload
): Promise<PriceCardApiRow> => {
  try {
    const response = await commonServerInstance.patch(
      `${PRICE_CARDS_PATH}/${id}`,
      payload
    )
    // Envelope: { code, message, data: <PriceCardApiRow> }
    return response?.data?.data
  } catch (error) {
    console.error('Update Price Card Error:', error)
    throw error
  }
}

// POST /api/v1/pricing/price-cards/validate — dry-run of the exact same
// checks POST /pricing/price-cards runs (field format, retroactive-edit
// lock, overlap/continuity, close-out preview) without writing anything.
// Lets the UI show what a create would do — in particular the close-out
// preview — before actually submitting it.
const PRICE_CARDS_VALIDATE_PATH = '/api/v1/pricing/price-cards/validate'

export interface PriceCardCloseOut {
  /** The existing record this create would close out (update the end
   * date of) for the same (billingOwner, zipcode) key. */
  id: number
  zipcode: string | null
  weightType: string | null
  weightUnit: string
  minWeight: number
  maxWeight: number
  /** The existing record's own effective start date — unaffected by the
   * close-out, shown so the UI can identify which record this is. */
  begDate: string
  /** The end date that record had before this close-out (e.g. "9999-12-31"
   * for a previously open-ended record). */
  originalEndDate: string
  /** The end date that record would be updated to. */
  endDate: string
}

export interface PriceCardValidationResult {
  valid: boolean
  /** True when begDate > endDate in the submitted payload — the record is
   * stored but never becomes active. */
  neverEffective: boolean
  /** e.g. "BILLING_OWNER_FALLBACK" — the resolved card type for this
   * submission (billing-owner-level vs. zipcode-level). */
  priceCardType: string
  /** The existing record(s) this submission would close out (update the
   * end date of) for the same (billingOwner, zipcode) key — empty when
   * none. An edit can close out at most its own prior state, but the
   * field is still an array to match the endpoint's shape. */
  closeOuts: PriceCardCloseOut[]
}

export interface ValidatePriceCardPayload extends CreatePriceCardPayload {
  /** The record being edited — excluded from the overlap/retroactive-edit
   * checks run against itself. Omit (or pass null) when validating a
   * brand-new, not-yet-created card. */
  id?: number | null
}

export const validatePriceCard = async (
  payload: ValidatePriceCardPayload
): Promise<PriceCardValidationResult> => {
  try {
    const response = await commonServerInstance.post(
      PRICE_CARDS_VALIDATE_PATH,
      payload
    )
    // Envelope: { code, message, data: <PriceCardValidationResult> }
    return response?.data?.data
  } catch (error) {
    console.error('Validate Price Card Error:', error)
    throw error
  }
}

// GET /api/v1/reference/zipcodes — valid zipcodes for a billing owner, for
// the "Zipcode" dropdown on the Create Price Card dialog (instead of
// letting the user free-type an arbitrary 5-digit string that may not
// belong to that owner). `dw_dim_billing_owner_zipcodes` is the sole
// source of truth for the zipcode-to-billingOwner mapping — no fallback
// tier. Cursor ("load more") pagination, not page-numbered: fetch the
// first batch with no cursor, then keep passing back the previous
// response's nextCursor while hasMore is true.
const REFERENCE_ZIPCODES_PATH = '/api/v1/reference/zipcodes'

export interface FetchZipcodesByBillingOwnerParams {
  billingOwner: string
  /** Filters to zipcodes containing this numeric substring (1-5 digits). */
  keyword?: string
  /** Opaque cursor from the previous response's nextCursor — omit for the
   * first batch. */
  cursor?: string
  /** Max zipcodes to return in this batch (server default: 20). */
  limit?: number
}

export interface ZipcodeReferenceRow {
  zipcode: string
  billingOwner: string
}

export interface FetchZipcodesByBillingOwnerResult {
  data: ZipcodeReferenceRow[]
  nextCursor: string | null
  hasMore: boolean
}

export const fetchZipcodesByBillingOwner = async (
  params: FetchZipcodesByBillingOwnerParams
): Promise<FetchZipcodesByBillingOwnerResult> => {
  try {
    const response = await commonServerInstance.get(REFERENCE_ZIPCODES_PATH, {
      params
    })
    // Envelope: { code, message, data: { data: [...], nextCursor, hasMore } }
    return response?.data?.data
  } catch (error) {
    console.error('Fetch Zipcodes By Billing Owner Error:', error)
    throw error
  }
}

// GET /api/v1/reference/billing-owners — list all known billing owners, for
// dropdowns/admin scoping. De-duplicated to one row per airport code
// (billing owner). Paginated the same way as the price-cards endpoint.
const BILLING_OWNERS_PATH = '/api/v1/reference/billing-owners'

export type BillingOwnerSortBy =
  | 'billingOwner'
  | 'zipcodeCount'
  | 'priceCardCount'
export type BillingOwnerSortDir = 'asc' | 'desc'

export interface FetchBillingOwnersParams {
  /** Free-text filter, matched against billingOwner (airport code), name, and shortName. */
  keyword?: string
  page?: number
  pageSize?: number
  sortBy?: BillingOwnerSortBy
  sortDir?: BillingOwnerSortDir
}

export interface BillingOwnerApiRow {
  billingOwner: string
  warehouseId: number
  name: string
  parentWarehouseId: number
  shortName: string
  timeZone: string
  belongProvince: string
  /** Count of zipcodes mapped to this billing owner. */
  zipcodeCount: number
  /** Count of price cards (zipcode-level + billing-owner-level) for this billing owner. */
  priceCardCount: number
}

export interface FetchBillingOwnersResult {
  data: BillingOwnerApiRow[]
  page: number
  pageSize: number
  total: number
}

export const fetchBillingOwners = async (
  params: FetchBillingOwnersParams
): Promise<FetchBillingOwnersResult> => {
  try {
    const response = await commonServerInstance.get(BILLING_OWNERS_PATH, {
      params
    })
    // Envelope: { code, message, data: { data: [...], page, pageSize, total } }
    return response?.data?.data
  } catch (error) {
    console.error('Fetch Billing Owners Error:', error)
    throw error
  }
}

// GET /api/v1/pricing/price-cards/zipcodes — paginated summary of
// zipcode-level price-card override counts, grouped by zipcode.
//
// Groups zipcode IS NOT NULL rows by zipcode in SQL and paginates on the
// distinct zipcode count, not the raw row count — used by the Billing
// Owner detail page's "Zipcodes" tab so it doesn't have to loop-fetch every
// price card row and group client-side (that used to be done in
// usePriceCardsByBillingOwner.ts; see useZipcodePriceCardSummary.ts and
// useOwnerLevelPriceCards.ts for the replacement).
//
// The billing_owner-level fallback row (zipcode IS NULL) is NOT part of
// this grouping — use GET /pricing/price-cards for that (see
// useOwnerLevelPriceCards.ts). Weight/Rate/Effective Period filters are
// intentionally not supported here.
const ZIPCODE_PRICE_CARD_SUMMARY_PATH = '/api/v1/pricing/price-cards/zipcodes'

export type ZipcodeSummarySortBy = 'zipcode' | 'rateCardCount'
export type ZipcodeSummarySortDir = 'asc' | 'desc'
/** Derived status, matching priceCardsMapping.ts's CardStatus but
 * lowercased — see deriveCardStatus() for the beg/endDate logic this
 * mirrors server-side. */
export type ZipcodeSummaryStatus = 'active' | 'expired' | 'future'

export interface FetchZipcodePriceCardSummaryParams {
  /** Billing owner (airport code), e.g. "LAX" — required by this endpoint. */
  billingOwner: string
  /** Substring match against the zipcode string (`LIKE '%keyword%'`). */
  keyword?: string
  /** rateCardCount only counts rows matching this status when set. */
  status?: ZipcodeSummaryStatus
  includeDeleted?: boolean
  page?: number
  pageSize?: number
  sortBy?: ZipcodeSummarySortBy
  sortDir?: ZipcodeSummarySortDir
}

export interface ZipcodePriceCardSummaryRow {
  zipcode: string
  /** Number of price-card rows (rate cards) for this zipcode — narrowed by
   * `status` when that filter is set. */
  rateCardCount: number
}

export interface FetchZipcodePriceCardSummaryResult {
  data: ZipcodePriceCardSummaryRow[]
  page: number
  pageSize: number
  /** Total distinct zipcodes matching the filters, not total rate cards. */
  total: number
}

export const fetchZipcodePriceCardSummary = async (
  params: FetchZipcodePriceCardSummaryParams
): Promise<FetchZipcodePriceCardSummaryResult> => {
  try {
    const response = await commonServerInstance.get(
      ZIPCODE_PRICE_CARD_SUMMARY_PATH,
      { params }
    )
    // Envelope: { code, message, data: { data: [...], page, pageSize, total } }
    return response?.data?.data
  } catch (error) {
    console.error('Fetch Zipcode Price Card Summary Error:', error)
    throw error
  }
}
